import React, { useState } from "react";

import DOMPurify from "dompurify";
import { CopyRegular, DocumentAddRegular } from "@fluentui/react-icons";
import { Button, makeStyles, Tooltip } from "@fluentui/react-components";

import { CqWord } from "@/utils/word";
import { Logo } from "@/components/Logo";
import { translate } from "@/utils/translations";
import { sanitizeOptions } from "@/utils/sanitize";

import { useMessageListStyles } from "./styles";
import TypewriterText from "./TypewritingMessage";

interface ReplaceMessageProps {
  content: string;
  typing?: boolean;
  onTypingComplete?: () => void;
  onTypingProgress?: () => void;
}

const ReplaceMessage: React.FC<ReplaceMessageProps> = ({
  content,
  onTypingComplete,
  onTypingProgress,
  typing,
}) => {
  const styles = useMessageListStyles();
  const buttonStyles = useButtonStyles();
  const replaceStyles = useReplaceStyles();
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Function to parse and style text with \startReplace and \endReplace markers
  const parseReplaceText = (text: string) => {
    // Find text between \startReplace and \endReplace markers
    const replaceRegex = /\\startReplace(.*?)\\endReplace/gs;

    return text.replace(replaceRegex, (_match, capturedText) => {
      return `<div class="${
        replaceStyles.replacementBox
      }">${capturedText.trim()}</div>`;
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setShowCopiedTooltip(true);
      setTimeout(() => setShowCopiedTooltip(false), 2000);
    } catch (error) {
      console.error("Failed to copy content:", error);
    }
  };

  const handleInsert = () => {
    try {
      CqWord.addHtmlText(content, "Replace");
    } catch (error) {
      console.error("Failed to insert content:", error);
    }
  };

  return (
    <div className={styles.assistantMessage}>
      <div className={styles.avatar}>
        <Logo size={18} withoutText />
        <p className={styles.avatarText}>Assistant</p>
      </div>
      <div className={styles.assistantMessageBubble}>
        {typing ? (
          <div className={replaceStyles.replaceContent}>
            <TypewriterText
              onComplete={onTypingComplete}
              onProgress={onTypingProgress}
              speed={5}
              text={content}
            />
          </div>
        ) : (
          <div
            className={replaceStyles.replaceContent}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                parseReplaceText(content),
                sanitizeOptions()
              ),
            }}
          />
        )}
        {typing && <span className={styles.typingIndicator}> ...</span>}
        {!typing && (
          <div className={buttonStyles.buttonContainer}>
            <Button
              appearance="transparent"
              className={`${buttonStyles.actionButton}`}
              icon={<DocumentAddRegular />}
              onClick={handleInsert}
              size="small"
            >
              {translate("Insert")}
            </Button>
            <Tooltip
              content={showCopiedTooltip ? "Copied!" : "Copy"}
              relationship="label"
              visible={showCopiedTooltip}
            >
              <Button
                appearance="transparent"
                className={`${buttonStyles.actionButton}`}
                icon={<CopyRegular />}
				onClick={handleCopy}
                size="small"
              >
                {translate("Copy")}
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplaceMessage;

const useButtonStyles = makeStyles({
//   actionButton: {
//     borderRadius: "6px",
//     fontSize: "12px",
//     height: "32px",
//     minWidth: "auto",
//     paddingLeft: "12px",
//     paddingRight: "12px",
//   },
  buttonContainer: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-start",
    marginTop: "12px",
  },
});

const useReplaceStyles = makeStyles({
  replaceContent: {
    color: "#323130",
    fontSize: "14px",
    lineHeight: "1.5",
  },
  replacementBox: {
    "&::before": {
      backgroundColor: "#0078d4",
      borderRadius: "4px",
      color: "white",
      content: "'Replacement'",
      fontSize: "11px",
      fontWeight: "600",
      left: "8px",
      padding: "2px 8px",
      position: "absolute",
      top: "-10px",
    },
    backgroundColor: "#f8f9fa",
    border: "2px solid #0078d4",
    borderRadius: "6px",
    margin: "8px 0",
    padding: "12px",
    position: "relative",
  },
});
